import { isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject, Injectable, PLATFORM_ID, signal, Type } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { switchMap } from 'rxjs';
import { AuthService } from '../../core/services/auth/auth.service';
import { DialogService } from '../../core/services/dialog.service';
import { StorageService } from '../../core/services/storage/storage.service';
import { ToastService } from '../../core/services/toast/toast.service';
import { Country, GlobalRegion, Region } from '../../interfaces/cached-content';
import { CheckoutData, Sim } from '../../interfaces/sims';
import { SubscriberService } from '../../ssr-services/subscriber/subscriber.service';
import { IdentityVerificationService } from '../identity-verification/identity-verification.service';
import { InventoryManagementService } from '../inventory-management/inventory-management.service';
import { SimsService } from '../sims/sims.service';

export interface NavigationContext {
  regionType: string;
  destinationSlug: string;
  planSlug: string;
  imsiId: string;
}

/**
 * Type for eSIM Selection Dialog Component
 * Components passed to the navigation service should be Angular component types
 * The actual component should have the following inputs:
 * - regionType: string
 * - destinationSlug: string
 * - planSlug: string
 * - imsiId: string
 * - inventoryAvailability: boolean
 */
export type EsimSelectionDialogComponentType = Type<unknown>;

export type AuthComponentType = Type<unknown>;

export enum SubscriberKycButtonStates {
  SubscriberLoading = 'subscriber-loading',
  GoToEsimSelection = 'go-to-esim-selection',
  GoToCheckout = 'go-to-checkout',
  OpenAuthModal = 'open-auth-modal',
  GoToVerification = 'go-to-verification',
}

@Injectable({
  providedIn: 'root',
})
export class EkycNavigationService {
  private readonly dialogService = inject(DialogService);
  protected authService = inject(AuthService);
  protected identityVerificationService = inject(IdentityVerificationService);
  protected simsService = inject(SimsService);
  protected storageService = inject(StorageService);
  protected inventoryManagementService = inject(InventoryManagementService);
  protected toastService = inject(ToastService);

  protected subscriberService = inject(SubscriberService);

  public SubscriberKycButtonStates = SubscriberKycButtonStates;

  protected translateService = inject(TranslateService);

  protected platformId = inject(PLATFORM_ID);

  protected router = inject(Router);

  public destination = signal<Country | Region | GlobalRegion | null>(null);

  shouldHandleButtonClick = false;

  public isCheckingInventory = signal<boolean>(false);

  public selectedEsim = signal<Sim | undefined>(undefined);

  public debounceSubscriberReadyState(
    context: NavigationContext,
    EsimSelectionDialogComponent: EsimSelectionDialogComponentType,
    AuthComponent: AuthComponentType
  ) {
    const subscriber = this.subscriberService.subscriber();
    if (this.shouldHandleButtonClick && subscriber && !subscriber.isGuest) {
      this.shouldHandleButtonClick = false;
      this.handleButtonClick(context, EsimSelectionDialogComponent, AuthComponent);
    }
  }

  public globalEKycEnabled = computed(
    () =>
      this.identityVerificationService?.isEKycEnabled(
        this.destination()?.eKycRequired || false
      )() ?? false
  );

  public userEKycStatus = computed(() => this.identityVerificationService?.eKycDetails()?.status);

  public isLoggedIn = this.authService.isAuthenticated;

  public buttonAction = computed<SubscriberKycButtonStates>(() => {
    const ekyc = this.globalEKycEnabled();
    const loggedIn = this.isLoggedIn();
    const status = this.userEKycStatus();

    const subscriber = this.subscriberService.subscriber();

    if (!ekyc) {
      return loggedIn
        ? SubscriberKycButtonStates.GoToEsimSelection
        : SubscriberKycButtonStates.GoToCheckout;
    }

    if (loggedIn) {
      if (subscriber.isGuest) {
        return SubscriberKycButtonStates.SubscriberLoading;
      }
      return status === 'success'
        ? SubscriberKycButtonStates.GoToEsimSelection
        : SubscriberKycButtonStates.GoToVerification;
    }

    return SubscriberKycButtonStates.OpenAuthModal;
  });

  storeCheckoutData(context: NavigationContext): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    const checkoutData: CheckoutData = {
      regionType: context.regionType,
      destinationSlug: context.destinationSlug,
      planSlug: context.planSlug,
      redirectUrl: `${context.destinationSlug}/${context.planSlug}`,
      imsiId: context.imsiId,
    };
    this.storageService.setCheckoutData(checkoutData);
  }

  openAuthModal(
    context: NavigationContext,
    AuthComponent: AuthComponentType,
    EsimSelectionDialogComponent: EsimSelectionDialogComponentType
  ) {
    this.dialogService
      .openModal(AuthComponent, {
        cssClass: 'modal-medium',
        modalId: 'signInModal',
        props: {
          source: 'kyc-verification',
          autoRedirectOnSocialLogin: true,
          postAuthRedirect: {
            url: `${context.destinationSlug}/${context.planSlug}`,
            queryParams: { source: 'kyc-verification' },
          },
        },
      })
      .subscribe((result) => {
        if (result === 'kyc-verification') {
          this.shouldHandleButtonClick = true;
          this.handleButtonClick(context, EsimSelectionDialogComponent, AuthComponent);
        }
      });
  }

  goToVerification(context: NavigationContext) {
    this.storeCheckoutData(context);
    this.dialogService.closeTopDialog();
    return this.router.navigate(['/verification']);
  }

  resolveAction(action: SubscriberKycButtonStates, imsiId: string): SubscriberKycButtonStates {
    if (action === SubscriberKycButtonStates.GoToEsimSelection) {
      // Check if eSIM is already selected from localStorage
      const esimData = this.storageService.getEsimData();
      if (esimData?.isEsimSelected || esimData?.isNewSim) {
        return SubscriberKycButtonStates.GoToCheckout;
      }

      // Check if any SIM is available with the specific imsiId
      const sims = this.simsService.sims();
      const hasSimWithImsiId = sims?.some((sim) => sim.imsiId?.toString() === imsiId);

      return hasSimWithImsiId
        ? SubscriberKycButtonStates.GoToEsimSelection
        : SubscriberKycButtonStates.GoToCheckout;
    }
    return action;
  }

  handleButtonClick(
    context: NavigationContext,
    EsimSelectionDialogComponent: EsimSelectionDialogComponentType,
    AuthComponent: AuthComponentType
  ) {
    // Clear previous eSIM selection for a fresh purchase flow.
    // The "Change" button bypasses handleButtonClick (calls goToEsimSelection directly),
    // so preselection is preserved only when reopening the dialog via "Change".
    this.selectedEsim.set(undefined);

    switch (this.resolveAction(this.buttonAction(), context.imsiId)) {
      case SubscriberKycButtonStates.SubscriberLoading:
        break;
      case SubscriberKycButtonStates.GoToEsimSelection:
        this.goToEsimSelection(context, EsimSelectionDialogComponent);
        break;
      case SubscriberKycButtonStates.GoToCheckout:
        this.goToCheckout(context);
        break;
      case SubscriberKycButtonStates.OpenAuthModal:
        // Note: AuthComponent needs to be passed from the theme
        this.openAuthModal(context, AuthComponent, EsimSelectionDialogComponent);
        break;
      case SubscriberKycButtonStates.GoToVerification:
        this.goToVerification(context);
        break;
    }
  }

  private openEsimSelectionDialog(
    context: NavigationContext,
    inventoryAvailability: boolean,
    EsimSelectionDialogComponent: EsimSelectionDialogComponentType
  ): void {
    this.dialogService.openModal(EsimSelectionDialogComponent, {
      cssClass: 'modal-small',
      modalId: 'esimSelectionModal',
      props: {
        regionType: context.regionType,
        destinationSlug: context.destinationSlug,
        planSlug: context.planSlug,
        imsiId: context.imsiId,
        inventoryAvailability: inventoryAvailability,
      },
    });
  }

  private navigateToCheckout(context: NavigationContext): void {
    this.router.navigate([`/${context.destinationSlug}/${context.planSlug}/checkout`]);
  }

  // Optimized code

  showErrorToast(message: string): void {
    this.toastService
      .openErrorToast(message)
      .pipe(switchMap((toast) => toast.present()))
      .subscribe();
  }

  private checkInventory(
    context: NavigationContext,
    onAvailable: () => void,
    onUnavailable: () => void,
    onError: (error: HttpErrorResponse) => void
  ): void {
    this.isCheckingInventory.set(true);

    this.inventoryManagementService.checkInventoryAvailability(context.imsiId).subscribe({
      next: (isAvailable) => {
        this.isCheckingInventory.set(false);
        isAvailable ? onAvailable() : onUnavailable();
      },
      error: (error: HttpErrorResponse) => {
        this.isCheckingInventory.set(false);
        onError(error);
      },
    });
  }

  goToEsimSelection(
    context: NavigationContext,
    EsimSelectionDialogComponent: EsimSelectionDialogComponentType
  ): void {
    this.checkInventory(
      context,
      () => this.openEsimSelectionDialog(context, true, EsimSelectionDialogComponent),
      () => this.handleInventoryUnavailable(context, EsimSelectionDialogComponent),
      (error) => this.handleInventoryCheckError(error)
    );
  }

  goToCheckout(context: NavigationContext): void {
    const esimData = this.storageService.getEsimData();

    if (
      this.isLoggedIn() &&
      !esimData?.topUpSubscriptionId &&
      !esimData?.isBuyAgain &&
      !esimData?.isNewPlan
    ) {
      this.checkInventory(
        context,
        () => this.navigateToCheckout(context),
        () => this.handleInventoryUnavailable(context),
        (error) => this.handleInventoryCheckError(error)
      );
    } else {
      this.navigateToCheckout(context);
    }
  }

  private handleInventoryUnavailable(
    context: NavigationContext,
    EsimSelectionDialogComponent?: EsimSelectionDialogComponentType
  ): void {
    const hasSimWithImsiId = this.simsService
      .sims()
      ?.some((sim) => sim.imsiId?.toString() === context.imsiId);

    if (hasSimWithImsiId && EsimSelectionDialogComponent) {
      this.openEsimSelectionDialog(context, false, EsimSelectionDialogComponent);
    } else {
      this.showErrorToast(
        this.translateService.instant('identity_verification.inventory_unavailable_description')
      );
    }
  }

  private handleInventoryCheckError(error: HttpErrorResponse): void {
    this.showErrorToast(error?.error?.message);
  }
}
